/**
 * Database connection test utility
 * Tests connectivity to Supabase database
 */
import { supabase } from '@/integrations/supabase/client';

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  details?: {
    url?: string;
    authenticated?: boolean;
    tablesAccessible?: boolean;
    error?: string;
  };
}

/**
 * Tests the database connection by attempting to query a simple table
 * @returns Promise with connection test results
 */
export const testDatabaseConnection = async (): Promise<ConnectionTestResult> => {
  try {
    // Test 1: Check if Supabase URL is configured
    const url = import.meta.env.VITE_SUPABASE_URL;
    
    if (!url) {
      return {
        success: false,
        message: 'Supabase URL not configured',
        details: {
          error: 'VITE_SUPABASE_URL environment variable is missing. Please set it in your .env file.',
        },
      };
    }

    // Test 2: Check authentication status
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    // Test 3: Try to query a table (participants table is likely to exist)
    // Using a simple select with limit 0 to test connection without fetching data
    const { error: queryError } = await supabase
      .from('participants')
      .select('id')
      .limit(0);

    // If queryError exists but it's not a "relation does not exist" error, connection is working
    const isConnectionWorking = !queryError || 
      queryError.message.includes('relation') || 
      queryError.message.includes('permission') ||
      queryError.code === 'PGRST116'; // Empty result is fine

    // Test 4: Try to query profiles table as alternative
    let tablesAccessible = false;
    if (queryError) {
      const { error: profilesError } = await supabase
        .from('profiles')
        .select('id')
        .limit(0);
      
      tablesAccessible = !profilesError || 
        profilesError.message.includes('relation') ||
        profilesError.code === 'PGRST116';
    } else {
      tablesAccessible = true;
    }

    if (isConnectionWorking || tablesAccessible) {
      return {
        success: true,
        message: 'Database connection successful',
        details: {
          url,
          authenticated: !!user,
          tablesAccessible: true,
        },
      };
    }

    return {
      success: false,
      message: 'Database connection failed',
      details: {
        url,
        authenticated: !!user,
        tablesAccessible: false,
        error: queryError?.message || 'Unknown error',
      },
    };
  } catch (error) {
    return {
      success: false,
      message: 'Database connection test failed',
      details: {
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      },
    };
  }
};

/**
 * Tests if we can perform a simple read operation
 */
export const testDatabaseRead = async (): Promise<ConnectionTestResult> => {
  try {
    // Try to read from books table (common table in this app)
    const { data, error } = await supabase
      .from('books')
      .select('id')
      .limit(1);

    if (error) {
      // Check if it's just a "no rows" or "table doesn't exist" error
      if (error.code === 'PGRST116' || error.message.includes('relation')) {
        return {
          success: true,
          message: 'Database connection working (table may not exist, but connection is valid)',
          details: {
            error: error.message,
          },
        };
      }

      return {
        success: false,
        message: 'Database read test failed',
        details: {
          error: error.message,
        },
      };
    }

    return {
      success: true,
      message: 'Database read test successful',
      details: {
        tablesAccessible: true,
      },
    };
  } catch (error) {
    return {
      success: false,
      message: 'Database read test error',
      details: {
        error: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
};

